import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Terminal, 
  Code, 
  Database, 
  Globe, 
  Server, 
  Shield, 
  Zap, 
  Award, 
  ChevronRight, 
  Mail, 
  Linkedin, 
  Heart,
  BookOpen,
  Gamepad,
  Hash,
  RefreshCw,
  Target,
  ArrowUp,
  MoveHorizontal,
} from 'lucide-react';

// --- GAME UTILITIES ---
const BOARD_SIZE = 20; // For Snake Game
const GAME_SPEED = 120; // milliseconds

// --- GAME COMPONENTS START ---

// 1. TIC-TAC-TOE COMPONENT
const calculateWinner = (squares) => {
  const lines = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6],
  ];
  for (let i = 0; i < lines.length; i++) {
    const [a, b, c] = lines[i];
    if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
      return squares[a];
    }
  }
  return null;
};

const TicTacToe = ({ onGameWin, showMessage }) => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);

  const winner = calculateWinner(board);
  const isDraw = !winner && board.every(Boolean);

  const handleClick = (i) => {
    if (winner || isDraw || board[i]) {
      return;
    }
    const newBoard = board.slice();
    newBoard[i] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  useEffect(() => {
    if (winner === 'X') {
      showMessage("T-T-T: VICTORY! +100 SCORE.");
      onGameWin(100);
    } else if (winner === 'O') {
      showMessage("T-T-T: DEFEAT! O won.");
    } else if (isDraw) {
      showMessage("T-T-T: DRAW.");
    }
  }, [winner, isDraw, onGameWin, showMessage]);

  const status = winner ? `Winner: ${winner}` : isDraw ? "Draw" : `Next Player: ${isXNext ? 'X' : 'O'}`;

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
  };

  const Square = ({ value, onClick }) => (
    <button
      className={`w-full h-full text-4xl font-retro flex items-center justify-center border-4 border-gray-700 hover:bg-gray-800 transition-colors duration-100 ${value === 'X' ? 'text-green-400' : 'text-red-400'}`}
      onClick={onClick}
    >
      {value}
    </button>
  );

  return (
    <div className="bg-gray-900 p-4 border border-green-700 shadow-xl">
      <h3 className="font-retro text-xl text-green-400 mb-4 flex items-center justify-between">
        TIC-TAC-TOE <span className="text-sm font-terminal bg-black p-1 text-white">{status}</span>
      </h3>
      <div className="grid grid-cols-3 gap-0.5 w-64 h-64 mx-auto bg-gray-700 border-2 border-gray-600">
        {board.map((value, i) => (
          <Square key={i} value={value} onClick={() => handleClick(i)} />
        ))}
      </div>
      <div className="text-center mt-4">
        <button 
          onClick={resetGame}
          className="retro-btn bg-gray-600 hover:bg-gray-500 text-black font-retro text-xs py-2 px-4 border border-gray-400 flex items-center mx-auto"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> RESTART
        </button>
      </div>
    </div>
  );
};

// 2. NUMBER GUESSING GAME COMPONENT
const GuessingGame = ({ onGameWin, showMessage }) => {
  const [targetNumber, setTargetNumber] = useState(Math.floor(Math.random() * 100) + 1);
  const [guess, setGuess] = useState('');
  const [message, setMessage] = useState('Enter a number between 1 and 100');
  const [attempts, setAttempts] = useState(0);
  const [gameStatus, setGameStatus] = useState('playing'); // playing, won

  const handleGuess = () => {
    const num = parseInt(guess, 10);
    if (isNaN(num) || num < 1 || num > 100) {
      setMessage('Invalid input. Must be 1-100.');
      return;
    }
    setAttempts(prev => prev + 1);

    if (num === targetNumber) {
      const reward = 50 + Math.max(0, 10 - attempts) * 10;
      setMessage(`CORRECT! Took ${attempts + 1} attempts. Reward: +${reward} SCORE.`);
      showMessage("G-GAME: Code Cracked! Reward granted.");
      onGameWin(reward);
      setGameStatus('won');
    } else if (num < targetNumber) {
      setMessage('Target is HIGHER.');
    } else {
      setMessage('Target is LOWER.');
    }
  };

  const resetGame = () => {
    setTargetNumber(Math.floor(Math.random() * 100) + 1);
    setGuess('');
    setMessage('Enter a number between 1 and 100');
    setAttempts(0);
    setGameStatus('playing');
  };

  return (
    <div className="bg-gray-900 p-4 border border-purple-700 shadow-xl min-h-[300px]">
      <h3 className="font-retro text-xl text-purple-400 mb-4 flex items-center gap-2">
        <Target className="w-5 h-5"/> ENCRYPTION BREAKER
      </h3>
      <p className="font-terminal text-lg text-gray-300 mb-4">{message}</p>
      
      {gameStatus === 'playing' ? (
        <div className="space-y-4">
          <input
            type="number"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            className="w-full bg-black border border-purple-600 text-green-400 font-terminal p-2 text-xl focus:ring-0 focus:border-purple-400"
            placeholder="[1..100]"
            disabled={gameStatus !== 'playing'}
          />
          <button 
            onClick={handleGuess}
            className="retro-btn w-full bg-purple-600 hover:bg-purple-500 text-black font-retro text-sm py-2 border border-purple-400"
          >
            ATTEMPT HACK
          </button>
          <p className="font-terminal text-sm text-gray-500">ATTEMPTS: {attempts}</p>
        </div>
      ) : (
        <div className="text-center mt-8">
            <button 
              onClick={resetGame}
              className="retro-btn bg-gray-600 hover:bg-gray-500 text-black font-retro text-xs py-2 px-4 border border-gray-400 flex items-center mx-auto"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> NEW TARGET
            </button>
        </div>
      )}
    </div>
  );
};

// 3. SNAKE GAME COMPONENT
const getRandomPosition = () => ({
  x: Math.floor(Math.random() * BOARD_SIZE),
  y: Math.floor(Math.random() * BOARD_SIZE),
});

const SnakeGame = ({ onGameWin, showMessage }) => {
  const initialSnake = [{ x: 10, y: 10 }, { x: 10, y: 11 }];
  const [snake, setSnake] = useState(initialSnake);
  const [food, setFood] = useState(getRandomPosition());
  const [direction, setDirection] = useState({ x: 0, y: -1 }); // Initial: moving up
  const [isGameOver, setIsGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(true); // Game starts paused
  const [score, setGameScore] = useState(0);

  const directionRef = useRef(direction);
  directionRef.current = direction;
  
  // Wrap resetGame in useCallback for stable reference
  const resetGame = useCallback(() => {
    setSnake(initialSnake);
    setFood(getRandomPosition());
    setDirection({ x: 0, y: -1 });
    setIsGameOver(false);
    setIsPaused(true);
    setGameScore(0);
  }, []);

  const handleKeyDown = useCallback((e) => {
    const key = e.key;
    
    // 1. Prevent default browser scrolling/actions for game keys
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', ' '].includes(key)) {
        e.preventDefault();
    }

    // 2. Handle Escape key for reset
    if (key === 'Escape') {
      e.preventDefault();
      resetGame();
      showMessage("SNAKE: System reset initiated.");
      return;
    }
    
    if (isGameOver) return;
    
    // 3. Handle Start/Unpause when pressing a movement key from initial/paused state
    const isMovementKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd'].includes(key);
    if (isPaused && isMovementKey) {
        setIsPaused(false);
    }

    const newDir = directionRef.current;
    
    // 4. Handle direction change
    switch (key) {
      case 'ArrowUp':
      case 'w':
        if (newDir.y !== 1) setDirection({ x: 0, y: -1 });
        break;
      case 'ArrowDown':
      case 's':
        if (newDir.y !== -1) setDirection({ x: 0, y: 1 });
        break;
      case 'ArrowLeft':
      case 'a':
        if (newDir.x !== 1) setDirection({ x: -1, y: 0 });
        break;
      case 'ArrowRight':
      case 'd':
        if (newDir.x !== -1) setDirection({ x: 1, y: 0 });
        break;
      case ' ':
        setIsPaused(p => !p);
        break;
      default:
        break;
    }
  }, [isGameOver, isPaused, resetGame, showMessage]);

  useEffect(() => {
    // Attach listener to the whole document
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]); // Dependency array includes the stable callback

  useEffect(() => {
    if (isGameOver || isPaused) return;

    const gameLoop = setInterval(() => {
      setSnake(prevSnake => {
        const newDirection = directionRef.current;
        const newHead = {
          x: prevSnake[0].x + newDirection.x,
          y: prevSnake[0].y + newDirection.y,
        };

        // 1. Check for wall collision
        if (
          newHead.x < 0 ||
          newHead.x >= BOARD_SIZE ||
          newHead.y < 0 ||
          newHead.y >= BOARD_SIZE
        ) {
          setIsGameOver(true);
          showMessage("SNAKE: Wall Collision. GAME OVER.");
          onGameWin(score * 2); // Small reward for score achieved
          return prevSnake;
        }

        // 2. Check for self collision
        if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
          setIsGameOver(true);
          showMessage("SNAKE: Self-Destruct. GAME OVER.");
          onGameWin(score * 2); 
          return prevSnake;
        }

        const newSnake = [newHead, ...prevSnake];

        // 3. Check for food
        if (newHead.x === food.x && newHead.y === food.y) {
          setFood(getRandomPosition());
          setGameScore(s => s + 10);
          showMessage("SNAKE: Data acquired. +10 Score.");
        } else {
          newSnake.pop(); // Remove tail only if food wasn't eaten
        }

        return newSnake;
      });
    }, GAME_SPEED);

    return () => clearInterval(gameLoop);
  }, [isGameOver, isPaused, food, onGameWin, showMessage, score]);


  const getCellClass = (x, y) => {
    if (snake.some(segment => segment.x === x && segment.y === y)) {
      return (snake[0].x === x && snake[0].y === y) ? 'bg-green-400 border border-green-600' : 'bg-green-700 border border-green-800';
    }
    if (food.x === x && food.y === y) {
      return 'bg-red-500 rounded-full animate-pulse border-2 border-red-300';
    }
    return '';
  };
  
  const getStatusText = () => {
    if (isGameOver) return 'GAME OVER';
    if (isPaused) return score === 0 ? '[PRESS SPACE TO START]' : '[PAUSED]';
    return '[RUNNING]';
  };

  return (
    <div className="bg-gray-900 p-4 border border-yellow-700 shadow-xl min-h-[300px] flex flex-col items-center">
      <h3 className="font-retro text-xl text-yellow-400 mb-4 flex items-center gap-2">
        <Hash className="w-5 h-5"/> SNAKE_GAME.EXE
      </h3>
      <div className="font-terminal text-sm text-gray-300 mb-2 flex justify-between w-64">
          <p>SCORE: <span className="text-white">{score}</span></p>
          <p className={isGameOver || isPaused ? "text-red-500 animate-pulse" : "text-green-500"}>
            {getStatusText()}
          </p>
      </div>

      {/* Game Board */}
      <div 
        className="grid border-2 border-yellow-500 shadow-lg"
        style={{
          width: '256px', // 20 * 12.8px
          height: '256px',
          gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
          gridTemplateRows: `repeat(${BOARD_SIZE}, 1fr)`,
          backgroundColor: '#0f0f0f'
        }}
        tabIndex={0}
      >
        {Array.from({ length: BOARD_SIZE * BOARD_SIZE }).map((_, i) => {
          const x = i % BOARD_SIZE;
          const y = Math.floor(i / BOARD_SIZE);
          return (
            <div 
              key={i} 
              className={`w-full h-full ${getCellClass(x, y)} transition-colors duration-50`} 
              style={{ padding: '1px' }} // Visual cell separation
            ></div>
          );
        })}
      </div>
      
      {/* Controls */}
      <div className="text-center mt-4 w-64 flex flex-col gap-2">
        {(isGameOver || isPaused) && (
            <button 
              onClick={resetGame}
              className="retro-btn bg-yellow-600 hover:bg-yellow-500 text-black font-retro text-xs py-2 px-4 border border-yellow-400 flex items-center justify-center flex-1"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> RESTART GAME
            </button>
        )}
        <p className="font-terminal text-xs text-gray-500 p-2 border border-gray-700">
          Arrows/WASD: Move | Space: Pause/Start | Esc: Reset
        </p>
      </div>
    </div>
  );
};

// 4. CONNECT FOUR COMPONENT
const ROWS = 6;
const COLS = 7;
const EMPTY = 0;
const PLAYER_1 = 1; // Red
const PLAYER_2 = 2; // Blue

const checkWin = (board, player) => {
  const checkLine = (r, c, dr, dc) => {
    let count = 0;
    for (let i = 0; i < 4; i++) {
      const nr = r + i * dr;
      const nc = c + i * dc;
      if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && board[nr][nc] === player) {
        count++;
      } else {
        break;
      }
    }
    return count === 4;
  };

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (board[r][c] === player) {
        // Check horizontal, vertical, and both diagonals
        if (
          checkLine(r, c, 0, 1) || // Horizontal
          checkLine(r, c, 1, 0) || // Vertical
          checkLine(r, c, 1, 1) || // Diagonal down-right
          checkLine(r, c, 1, -1)    // Diagonal down-left
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

const ConnectFour = ({ onGameWin, showMessage }) => {
  const initialBoard = Array(ROWS).fill(null).map(() => Array(COLS).fill(EMPTY));
  const [board, setBoard] = useState(initialBoard);
  const [currentPlayer, setCurrentPlayer] = useState(PLAYER_1);
  const [winner, setWinner] = useState(null);
  const [isDraw, setIsDraw] = useState(false);

  const resetGame = () => {
    setBoard(initialBoard);
    setCurrentPlayer(PLAYER_1);
    setWinner(null);
    setIsDraw(false);
  };

  const handleDrop = (col) => {
    if (winner || isDraw) return;

    // Find the lowest empty row in the column
    let newRow = -1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (board[r][col] === EMPTY) {
        newRow = r;
        break;
      }
    }

    if (newRow !== -1) {
      const newBoard = board.map(row => [...row]);
      newBoard[newRow][col] = currentPlayer;
      setBoard(newBoard);

      if (checkWin(newBoard, currentPlayer)) {
        setWinner(currentPlayer);
        if (currentPlayer === PLAYER_1) {
          showMessage("CONNECT 4: Player 1 (RED) VICTORY! +200 SCORE.");
          onGameWin(200);
        } else {
          showMessage("CONNECT 4: Player 2 (BLUE) DEFEAT.");
        }
      } else if (newBoard.every(row => row.every(cell => cell !== EMPTY))) {
        setIsDraw(true);
        showMessage("CONNECT 4: DRAW. Grid full.");
      } else {
        setCurrentPlayer(currentPlayer === PLAYER_1 ? PLAYER_2 : PLAYER_1);
      }
    }
  };
  
  const getStatus = () => {
    if (winner) {
      return `WINNER: PLAYER ${winner === PLAYER_1 ? 'RED' : 'BLUE'}`;
    }
    if (isDraw) {
      return 'DRAW GAME';
    }
    return `NEXT: PLAYER ${currentPlayer === PLAYER_1 ? 'RED' : 'BLUE'}`;
  };

  return (
    <div className="bg-gray-900 p-4 border border-blue-700 shadow-xl flex flex-col items-center">
      <h3 className="font-retro text-xl text-blue-400 mb-4 flex items-center gap-2">
        <MoveHorizontal className="w-5 h-5"/> CONNECT_4.EXE
      </h3>
      
      <div className="font-terminal text-sm text-gray-300 mb-4 flex items-center gap-2">
        STATUS: <span className={winner ? 'text-yellow-400' : 'text-white'}>{getStatus()}</span>
        <div className={`w-3 h-3 rounded-full ${currentPlayer === PLAYER_1 ? 'bg-red-500' : 'bg-blue-500'} ${!winner && !isDraw ? 'animate-pulse' : 'hidden'}`}></div>
      </div>
      
      {/* Drop Buttons */}
      <div className="grid gap-0 w-[300px]" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
        {Array.from({ length: COLS }).map((_, col) => (
          <button
            key={col}
            onClick={() => handleDrop(col)}
            disabled={winner || isDraw || board[0][col] !== EMPTY}
            className={`w-full h-8 flex items-center justify-center text-xs font-terminal text-gray-400 hover:text-white ${winner || isDraw || board[0][col] !== EMPTY ? 'opacity-30' : 'hover:bg-gray-700/50'}`}
            title={`Drop piece in column ${col + 1}`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        ))}
      </div>

      {/* Game Board */}
      <div 
        className="grid border-4 border-blue-500 bg-blue-800/80 p-1 shadow-inner"
        style={{
          width: '300px',
          height: `${(300 / COLS) * ROWS}px`, // Maintains aspect ratio for 6x7 grid
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          gridTemplateRows: `repeat(${ROWS}, 1fr)`,
        }}
      >
        {board.flat().map((cell, i) => (
          <div key={i} className="flex items-center justify-center p-0.5">
            <div 
              className={`w-full h-full rounded-full transition-colors duration-200 shadow-inner ${
                cell === PLAYER_1 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]' : 
                cell === PLAYER_2 ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]' : 
                'bg-gray-700/50'
              }`}
            ></div>
          </div>
        ))}
      </div>

      <div className="text-center mt-4">
        <button 
          onClick={resetGame}
          className="retro-btn bg-gray-600 hover:bg-gray-500 text-black font-retro text-xs py-2 px-4 border border-gray-400 flex items-center mx-auto"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> RESTART GAME
        </button>
      </div>
    </div>
  );
};

// --- GAME COMPONENTS END ---


const QUEST_DATA = [
    { id: 1, title: "Alakazzam LLC", status: 'current', reward: 500, label: "CURRENT QUEST", color: "green", icon: Terminal },
    { id: 2, title: "ForeMedia", status: 'completed', reward: 350, label: "RANK: 5 STARS", color: "yellow", icon: Globe },
    { id: 3, title: "Ardent Computech", status: 'completed', reward: 200, label: "BOSS DEFEATED", color: "blue", icon: Server },
    { id: 4, title: "BetaCloud", status: 'completed', reward: 100, label: "ORIGIN STORY", color: "purple", icon: Code }
];

const App = () => {
  const [loading, setLoading] = useState(true);
  const [typedText, setTypedText] = useState('');
  const fullText = " INITIALIZING SYSTEM... LOADING PROFILE: DIBYENDU SAHA...";
  const [activeSection, setActiveSection] = useState('hero');
  
  // Game State
  const [score, setScore] = useState(0);
  const [stressLevel, setStressLevel] = useState(0); // 0-100
  const [questStatus, setQuestStatus] = useState(QUEST_DATA.map(q => ({ id: q.id, status: q.status })));
  const [achievementUnlocked, setAchievementUnlocked] = useState(false);
  const [message, setMessage] = useState('');

  // --- LOCAL STORAGE & INITIAL LOAD ---

  useEffect(() => {
    // Load state from localStorage
    const savedScore = localStorage.getItem('ds_score');
    const savedStress = localStorage.getItem('ds_stress');
    const savedQuests = localStorage.getItem('ds_quest_status');
    const savedAchieve = localStorage.getItem('ds_achievement_unlocked');

    if (savedScore) setScore(parseInt(savedScore, 10));
    if (savedStress) setStressLevel(parseInt(savedStress, 10));
    if (savedQuests) setQuestStatus(JSON.parse(savedQuests));
    if (savedAchieve) setAchievementUnlocked(savedAchieve === 'true');

    // Start typing effect
    let index = 0;
    const timer = setInterval(() => {
      setTypedText((prev) => prev + fullText.charAt(index));
      index++;
      if (index === fullText.length) {
        clearInterval(timer);
        setTimeout(() => setLoading(false), 800);
      }
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('ds_score', score.toString());
    localStorage.setItem('ds_stress', stressLevel.toString());
    localStorage.setItem('ds_quest_status', JSON.stringify(questStatus));
    localStorage.setItem('ds_achievement_unlocked', achievementUnlocked.toString());
  }, [score, stressLevel, questStatus, achievementUnlocked]);
  
  // --- UTILITY FUNCTIONS ---

  const showMessage = useCallback((text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  }, []);
  
  // Arcade game win handler
  const handleGameWin = useCallback((reward) => {
    setScore(prev => prev + reward);
  }, []);


  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  // --- GAME LOGIC (PORTFOLIO) ---

  // 1. STATS: Stress Interaction
  const handleLikeClick = () => {
    if (stressLevel >= 100) {
      showMessage("WARNING: Stress level maxed! Take a break.");
      return;
    }
    const newStress = Math.min(100, stressLevel + 10);
    setStressLevel(newStress);
    showMessage(`STRESS_INCREASED: +10. Efficiency boosted temporarily.`);
  };

  const getAdjustedStat = (base) => {
    // Stress gives a slight temporary boost to performance up to a point
    const maxBoost = 10;
    const stressFactor = Math.min(1, stressLevel / 50); // Max factor 1 at 50 stress
    const adjustment = Math.floor(maxBoost * stressFactor);
    return Math.min(100, base + adjustment);
  };

  const CORE_STATS = [
    { label: "Backend Architecture", base: 95, color: "bg-red-500" },
    { label: "Frontend UI/UX", base: 90, color: "bg-blue-500" },
    { label: "DevOps / Cloud", base: 88, color: "bg-purple-500" },
    { label: "Problem Solving", base: 99, color: "bg-yellow-500" }
  ];

  // 2. QUESTS: Completion and Reward
  const handleClaimReward = (questId, reward) => {
    const status = questStatus.find(q => q.id === questId);
    // Check if the quest has been claimed. Only the current one (id 1) is not "claimed" by default.
    if (status && status.status === 'completed' && questId !== 1) {
      showMessage(`QUEST_COMPLETE: Reward already claimed.`);
      return;
    }
    
    // Set status to completed (and claimed)
    setQuestStatus(prev => prev.map(q => q.id === questId ? { ...q, status: 'completed' } : q));
    setScore(prev => prev + reward);
    showMessage(`REWARD_CLAIMED: +${reward} SCORE!`);
  };

  // 3. ACHIEVEMENTS: Unlock Bonus
  const handleUnlockAchievement = () => {
    if (achievementUnlocked) {
      showMessage("ACHIEVEMENT_LOGGED: Cannot unlock again.");
      return;
    }
    setAchievementUnlocked(true);
    setScore(prev => prev + 1000);
    showMessage("SECRET_ACHIEVEMENT_UNLOCKED! +1000 BONUS SCORE.");
  };


  // --- RENDERING ---

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-green-500 font-mono flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl border-4 border-green-500 p-8 shadow-[0_0_20px_rgba(34,197,94,0.4)] relative overflow-hidden bg-gray-900">
          <div className="absolute top-0 left-0 w-full h-1 bg-green-500/30 animate-scanline"></div>
          <p className="text-xl md:text-2xl mb-4 blink-cursor font-terminal"> {typedText}</p>
          <div className="w-full bg-gray-800 h-4 border-2 border-green-700 mt-8">
            <div className="h-full bg-green-500 animate-progress-load"></div>
          </div>
          <p className="mt-2 text-xs text-green-700 font-terminal">MEMORY_CHECK: OK | CPU: OPTIMAL | DEPLOY_READY</p>
        </div>
        <style>{`
          @keyframes progress-load {
            0% { width: 0%; }
            100% { width: 100%; }
          }
          .animate-progress-load {
            animation: progress-load 3s ease-in-out forwards;
          }
          @keyframes scanline {
            0% { transform: translateY(-100%); }
            100% { transform: translateY(2000%); }
          }
          .animate-scanline {
            animation: scanline 5s linear infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-gray-300 font-mono selection:bg-green-500 selection:text-black overflow-x-hidden">
      {/* GLOBAL STYLES & ANIMATIONS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=VT323&display=swap');
        
        .font-retro { font-family: 'Press Start 2P', cursive; }
        .font-terminal { font-family: 'VT323', monospace; }
        
        .scanline {
          background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0) 50%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.2));
          background-size: 100% 4px;
          position: fixed;
          pointer-events: none;
          top: 0; left: 0; right: 0; bottom: 0;
          z-index: 50;
          opacity: 0.15;
        }
        
        .crt-flicker {
          animation: flicker 0.15s infinite;
        }

        @keyframes flicker {
          0% { opacity: 0.97; }
          50% { opacity: 1; }
          100% { opacity: 0.98; }
        }

        .blink-cursor:after {
          content: '█';
          animation: blink 1s step-end infinite;
        }

        @keyframes blink {
          50% { opacity: 0; }
        }

        .retro-btn {
          box-shadow: 4px 4px 0px 0px #166534; /* green-800 */
          transition: all 0.1s;
        }
        .retro-btn:active {
          transform: translate(2px, 2px);
          box-shadow: 2px 2px 0px 0px #166534;
        }
      `}</style>

      <div className="scanline"></div>

      {/* HEADER / HUD */}
      <header className="fixed top-0 w-full z-40 bg-black/90 border-b-2 border-green-800 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="font-retro text-xs md:text-sm text-green-500 tracking-widest">DS_OS v2.6</span>
          </div>
          
          <div className="flex items-center gap-4 font-terminal text-lg text-white">
             {/* SCORE DISPLAY */}
             <div className="flex items-center gap-2 px-3 py-1 bg-green-900/30 border border-green-700">
                <BookOpen className="w-5 h-5 text-green-400" />
                <span className="text-green-400">SCORE:</span> <span className="text-white">{score}</span>
             </div>
          </div>

          <nav className="hidden md:flex gap-6 font-terminal text-xl">
            {['Stats', 'Quests', 'Arcade', 'Loot', 'Connect'].map((item) => (
              <button 
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="hover:text-green-400 hover:underline decoration-2 underline-offset-4 uppercase tracking-wide transition-colors"
              >
                [{item}]
              </button>
            ))}
          </nav>

          <button 
            onClick={() => scrollToSection('connect')}
            className="md:hidden bg-green-600 text-black font-bold px-2 py-1 text-xs font-retro"
          >
            HIRE
          </button>
        </div>

        {/* Dynamic Message Box */}
        {message && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 bg-yellow-900/90 border-2 border-yellow-400 text-yellow-100 font-terminal px-4 py-2 text-sm z-50 shadow-xl animate-bounce">
            {message}
          </div>
        )}
      </header>

      <main className="pt-20 pb-12 px-4 max-w-7xl mx-auto space-y-24 crt-flicker">
        
        {/* HERO SECTION: START GAME */}
        <section id="hero" className="min-h-[85vh] flex flex-col justify-center items-center text-center relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-green-900/10 via-black to-black -z-10"></div>
          
          <div className="border-2 border-green-500/30 p-2 inline-block mb-6 bg-black/50 backdrop-blur">
            <span className="font-terminal text-xl text-green-400 uppercase tracking-widest px-4">
              System Status: Ready for Deployment
            </span>
          </div>

          <h1 className="font-retro text-3xl md:text-5xl lg:text-6xl text-white mb-6 leading-relaxed md:leading-tight">
            <span className="text-green-500 text-shadow-neon">FULL-STACK</span> ARCHITECT<br/>
            <span className="text-sm md:text-2xl text-gray-400 mt-4 block font-terminal">Level Max // DevOps Specialist</span>
          </h1>

          <p className="font-terminal text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            "Architecting scalable digital empires with 99.9% uptime. 
            Ready to compile your vision into reality."
          </p>

          <div className="flex flex-col md:flex-row gap-6">
            <button 
              onClick={() => scrollToSection('quests')}
              className="retro-btn bg-green-600 hover:bg-green-500 text-black font-retro text-sm py-4 px-8 border-2 border-green-400 uppercase"
            >
              Start Quest
            </button>
            <button 
              onClick={() => scrollToSection('stats')}
              className="retro-btn bg-transparent hover:bg-gray-900 text-green-500 font-retro text-sm py-4 px-8 border-2 border-green-600 uppercase"
            >
              View Stats
            </button>
          </div>

          <div className="mt-16 animate-bounce text-gray-600">
            <ChevronRight className="w-8 h-8 rotate-90" />
          </div>
        </section>


        {/* ABOUT: PLAYER STATS (The Stress Game) */}
        <section id="stats" className="scroll-mt-24">
          <div className="border-4 border-gray-800 bg-gray-900/50 p-6 md:p-8 relative">
            {/* Decorative Corner Pixels */}
            <div className="absolute top-0 left-0 w-4 h-4 bg-green-500"></div>
            <div className="absolute top-0 right-0 w-4 h-4 bg-green-500"></div>
            <div className="absolute bottom-0 left-0 w-4 h-4 bg-green-500"></div>
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500"></div>

            <h2 className="font-retro text-2xl md:text-3xl text-green-400 mb-8 border-b-4 border-green-500/20 pb-4 inline-block">
              PLAYER_STATS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gray-800 p-3 border border-gray-600">
                    <img 
                      src="https://gravatar.com/avatar/306c2b77f78b9bea86b76566c8809a98?s=200" 
                      alt="Avatar" 
                      className="w-24 h-24 grayscale contrast-125 hover:grayscale-0 transition-all"
                      onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/100x100/374151/ffffff?text=AVATAR_LOAD_FAIL"; }}
                    />
                  </div>
                  <div className="font-terminal text-lg space-y-2">
                    <p><span className="text-gray-500">CLASS:</span> <span className="text-white">Technomancer (Full Stack)</span></p>
                    <p><span className="text-gray-500">LEVEL:</span> <span className="text-yellow-400">5 (Years Exp)</span></p>
                    <p><span className="text-gray-500">STRESS:</span> <span className={`${stressLevel > 70 ? 'text-red-500 animate-pulse' : 'text-green-500'}`}>{stressLevel}%</span></p>
                    <button 
                      onClick={handleLikeClick}
                      className="mt-2 retro-btn bg-red-600 text-white font-retro text-xs py-1 px-3 border border-red-400 flex items-center gap-1"
                      title="Simulate client requests or deadline pressure"
                    >
                        <Heart className="w-4 h-4 fill-white" />
                        ADD STRESS (LIKE)
                    </button>
                  </div>
                </div>

                <div className="bg-black border border-green-900 p-4 font-terminal text-lg text-green-300">
                  <p className="mb-2 text-white/50">// STRESS_METER_LOGIC:</p>
                  <p>Current stress level is {stressLevel}. High stress grants a slight temporary boost to base stats up to +10, but high levels {'>'}70 may risk burnout.</p>
                </div>
              </div>

              <div className="space-y-4 font-terminal text-xl">
                <h3 className="text-white mb-4 border-b border-gray-700 pb-1">CORE ATTRIBUTES</h3>
                
                {CORE_STATS.map(stat => {
                  const val = getAdjustedStat(stat.base);
                  const isBoosted = val > stat.base;
                  return (
                    <div key={stat.label}>
                      <div className="flex justify-between text-sm mb-1 text-gray-400">
                        <span>{stat.label}</span>
                        <span className={isBoosted ? 'text-yellow-400 flex items-center gap-1 animate-pulse' : ''}>
                           {isBoosted && <ArrowUp className="w-3 h-3" />}
                           {val}/100
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 h-4 border border-gray-600">
                        <div 
                          className={`h-full ${stat.color} transition-all duration-500`} 
                          style={{ width: `${val}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}

                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-gray-800 border border-gray-700 p-3 text-center">
                    <div className="text-3xl text-yellow-400 font-bold">100%</div>
                    <div className="text-xs text-gray-500 uppercase">Job Success (Upwork)</div>
                  </div>
                  <div className="bg-gray-800 border border-gray-700 p-3 text-center">
                    <div className="text-3xl text-green-400 font-bold">5★</div>
                    <div className="text-xs text-gray-500 uppercase">Client Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* EXPERIENCE: QUEST HISTORY */}
        <section id="quests" className="scroll-mt-24">
           <h2 className="font-retro text-2xl md:text-3xl text-green-400 mb-10 text-center">
            <span className="text-white">&lt;</span> QUEST_HISTORY <span className="text-white">/&gt;</span>
          </h2>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-700 before:to-transparent">
            
            {QUEST_DATA.map((quest) => {
                const status = questStatus.find(q => q.id === quest.id)?.status;
                const isClaimed = status === 'completed';
                const IconComponent = quest.icon;
                
                // Only quest 1 is available for claiming if it is not already claimed
                const isClaimable = quest.id === 1 && !isClaimed;
                
                return (
                    <div key={quest.id} className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group ${isClaimed ? 'opacity-70' : ''}`}>
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${isClaimed ? 'border-gray-500' : `border-${quest.color}-500`} bg-black shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 shadow-[0_0_15px_rgba(34,197,94,0.5)]`}>
                        <IconComponent className={`w-5 h-5 ${isClaimed ? 'text-gray-500' : `text-${quest.color}-500`}`} />
                      </div>
                      <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-gray-900 p-6 shadow-lg hover:bg-gray-800 transition-colors border-l-4 ${isClaimed ? 'border-gray-500' : `border-${quest.color}-500`}`}>
                        <div className="flex justify-between items-start mb-2">
                          <h3 className={`font-retro text-sm text-${quest.color}-300`}>{quest.title}</h3>
                          <span className={`text-xs font-terminal bg-${quest.color}-900/30 text-${quest.color}-400 px-2 py-1 rounded border border-${quest.color}-800`}>{quest.label}</span>
                        </div>
                        <h4 className="font-bold text-white mb-2">{quest.id === 1 ? 'Full-stack Developer' : quest.id === 2 ? 'Mercenary Developer' : quest.id === 3 ? 'Project Engineer' : 'Assistant Developer'}</h4>
                        <p className="text-xs text-gray-500 font-terminal mb-4">{quest.id === 1 ? 'Jul 2022 - Present' : quest.id === 2 ? 'Jun 2021 - Jul 2022' : quest.id === 3 ? 'Feb 2020 - May 2021' : 'Jun 2019 - Jan 2020'}</p>
                        
                        {isClaimed ? (
                            <p className="font-retro text-sm text-green-500 mt-4">REWARD CLAIMED: +{quest.reward} EXP</p>
                        ) : (
                             <button
                                onClick={() => handleClaimReward(quest.id, quest.reward)}
                                className={`retro-btn bg-${quest.color}-600 text-black font-retro text-xs py-2 px-3 border border-${quest.color}-400`}
                                disabled={!isClaimable} // Only the current quest (ID 1) should be claimable to simulate progression
                            >
                                {isClaimable ? `CLAIM REWARD (+${quest.reward} Score)` : 'ACTIVE'}
                            </button>
                        )}
                      </div>
                    </div>
                );
            })}
          </div>
        </section>


        {/* NEW ARCADE SECTION (Replaces Skills) */}
        <section id="arcade" className="scroll-mt-24">
          <div className="bg-[#111] border-2 border-gray-700 p-6 md:p-10">
            <h2 className="font-retro text-2xl text-center text-red-400 mb-12 flex items-center justify-center gap-3">
              <Gamepad className="w-8 h-8"/> ARCADE_TERMINAL_V1.0
            </h2>
            <p className="font-terminal text-center text-yellow-400 mb-8">
                TEST YOUR LOGIC CIRCUITS AND EARN BONUS SCORE!
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <TicTacToe onGameWin={handleGameWin} showMessage={showMessage} />
              <GuessingGame onGameWin={handleGameWin} showMessage={showMessage} />
              
              {/* Snake Game implemented */}
              <SnakeGame onGameWin={handleGameWin} showMessage={showMessage} />
              
              {/* Connect Four implemented */}
              <ConnectFour onGameWin={handleGameWin} showMessage={showMessage} />
            </div>
          </div>
        </section>


        {/* PROJECTS: ACHIEVEMENTS (The Bonus Game) */}
        <section id="loot" className="scroll-mt-24">
          <h2 className="font-retro text-2xl md:text-3xl text-yellow-400 mb-10 flex items-center gap-4">
            <Award className="w-8 h-8" /> 
            ACHIEVEMENTS_UNLOCKED
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                title: "TinTong Grocery", 
                type: "App Development", 
                loot: "+Custom Web Panel, +WooCommerce Sync",
                tech: "MERN / WordPress",
                icon: <Zap className="w-6 h-6 text-yellow-500" />
              },
              { 
                title: "3CX Plugin", 
                type: "Telephony Integration", 
                loot: "+IVR Flow Logic, +Call Traffic Routing",
                tech: "C# / .NET / API",
                icon: <Server className="w-6 h-6 text-blue-500" />
              },
              { 
                title: "MintShint CRM", 
                type: "SaaS Platform", 
                loot: "+Multi-site Admin, +Content Management",
                tech: "PHP / MySQL",
                icon: <Database className="w-6 h-6 text-purple-500" />
              },
              { 
                title: "Exam System", 
                type: "EdTech Portal", 
                loot: "+Video Proctoring, +Real-time Monitoring",
                tech: "Full Stack",
                icon: <Shield className="w-6 h-6 text-green-500" />
              },
              { 
                title: "Billing System", 
                type: "FinTech", 
                loot: "+Real-time Sync, +Tax Logic",
                tech: "React / Node",
                icon: <Code className="w-6 h-6 text-cyan-500" />
              },
              { 
                title: "Push Notify", 
                type: "Microservice", 
                loot: "+Analytics Dashboard, +User Segmentation",
                tech: "JavaScript / API",
                icon: <Terminal className="w-6 h-6 text-red-500" />
              }
            ].map((project, idx) => (
              <div key={idx} className="bg-gray-900 border-2 border-gray-800 p-5 hover:border-gray-500 hover:-translate-y-1 transition-all group">
                <div className="flex justify-between items-start mb-4">
                  <div className="bg-black border border-gray-700 p-2 rounded">
                    {project.icon}
                  </div>
                  <span className="text-[10px] font-retro bg-gray-800 px-2 py-1 text-gray-400">{project.type}</span>
                </div>
                <h3 className="font-bold text-white text-lg mb-2 group-hover:text-green-400 transition-colors">{project.title}</h3>
                <p className="font-terminal text-sm text-gray-400 mb-3">{project.tech}</p>
                <div className="border-t border-gray-800 pt-3">
                  <p className="font-terminal text-yellow-500 text-sm">
                    LOOT: {project.loot}
                  </p>
                </div>
              </div>
            ))}
            
            {/* The Secret Achievement Slot */}
            <div className={`bg-gray-900 border-2 ${achievementUnlocked ? 'border-yellow-400 animate-pulse' : 'border-dashed border-gray-700'} p-5 flex flex-col justify-center items-center text-center`}>
                <div className="mb-4">
                    {achievementUnlocked ? (
                        <Award className="w-10 h-10 text-yellow-400 fill-yellow-900" />
                    ) : (
                        <Shield className="w-10 h-10 text-gray-600" />
                    )}
                </div>
                <h3 className={`font-bold ${achievementUnlocked ? 'text-yellow-400' : 'text-gray-500'} text-lg mb-3`}>
                    {achievementUnlocked ? 'DEBUGGER MASTER' : 'HIDDEN ACHIEVEMENT'}
                </h3>
                <p className="font-terminal text-sm text-gray-400 mb-4">
                    {achievementUnlocked ? "The secret code has been executed. Bonus granted." : "Execute the secret code to unlock +1000 Score."}
                </p>
                <button
                    onClick={handleUnlockAchievement}
                    disabled={achievementUnlocked}
                    className={`retro-btn font-retro text-xs py-2 px-4 ${achievementUnlocked ? 'bg-gray-600 border-gray-500' : 'bg-red-600 border-red-400 hover:bg-red-500'}`}
                >
                    {achievementUnlocked ? 'LOGGED' : 'HACK TERMINAL'}
                </button>
            </div>
          </div>
        </section>


        {/* CONTACT: FINAL BOSS ROOM */}
        <section id="connect" className="bg-green-900/10 border-4 border-green-600 p-8 md:p-12 text-center relative overflow-hidden">
          {/* Animated Background Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,0,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          
          <div className="relative z-10">
            <h2 className="font-retro text-2xl md:text-4xl text-white mb-4 text-shadow-neon">
              READY TO DEPLOY?
            </h2>
            <p className="font-terminal text-xl text-green-300 mb-8 max-w-xl mx-auto">
              Your quest needs a high-level architect. Recruit Dibyendu Saha for your next mission.
            </p>

            <div className="flex flex-col md:flex-row justify-center gap-6 mb-12">
              <a href="mailto:contact@dibyendusaha.com" className="group retro-btn bg-green-600 text-black px-8 py-4 font-retro text-sm uppercase flex items-center justify-center gap-3 hover:bg-green-500">
                <Mail className="w-5 h-5" />
                <span>Send Intel</span>
              </a>
              <a href="https://linkedin.com/in/dsaha1656" target="_blank" rel="noreferrer" className="group retro-btn bg-blue-700 text-white px-8 py-4 font-retro text-sm uppercase flex items-center justify-center gap-3 hover:bg-blue-600 border-2 border-blue-500">
                <Linkedin className="w-5 h-5" />
                <span>Connect on Network</span>
              </a>
            </div>

            <div className="inline-block bg-black border border-green-800 p-4 font-terminal text-green-500 text-sm">
              <p className="animate-pulse">_ WAITING_FOR_INPUT...</p>
            </div>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="border-t border-gray-800 bg-black py-8 text-center font-terminal text-gray-500">
        <p>© 2025 DIBYENDU SAHA | SCORE: {score} | <span className="text-green-600 cursor-pointer hover:underline" onClick={() => scrollToSection('hero')}>INSERT COIN TO RESTART</span></p>
      </footer>
    </div>
  );
};

export default App;
